import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, DownloadSimple, Prohibit } from "@phosphor-icons/react";

/**
 * DeliveryMenu — the Menu subtab under Settings > Delivery.
 *
 * Renders the whole menu grouped by category with a per-item toggle. Two
 * bulk actions at the top: "Import Entire Menu" (add every item to
 * delivery) and "Clear All" (remove every item). Search filters live.
 */
const DeliveryMenu = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/delivery/menu`);
      setItems(res.data || []);
    } catch (e) {
      toast.error("Couldn't load menu");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (item, next) => {
    setSavingId(item.id);
    // optimistic
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, available_for_delivery: next } : it)));
    try {
      await axios.put(`${API}/delivery/menu/toggle`, {
        item_id: item.id,
        available_for_delivery: next,
      });
    } catch (e) {
      // rollback
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, available_for_delivery: !next } : it)));
      toast.error("Failed to update");
    } finally {
      setSavingId(null);
    }
  };

  const bulk = async (action) => {
    try {
      const res = await axios.put(`${API}/delivery/menu/bulk`, { action });
      toast.success(`${res.data.updated} item(s) updated`);
      load();
    } catch (e) {
      toast.error("Bulk action failed");
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (it) =>
        (it.name || "").toLowerCase().includes(needle) ||
        (it.category || "").toLowerCase().includes(needle),
    );
  }, [items, q]);

  const byCategory = useMemo(() => {
    const groups = {};
    for (const it of filtered) {
      const cat = it.category || "uncategorized";
      (groups[cat] = groups[cat] || []).push(it);
    }
    // Alphabetize categories and items for consistency.
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([cat, list]) => [cat, [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""))]);
  }, [filtered]);

  const enabledCount = items.filter((it) => it.available_for_delivery !== false).length;

  return (
    <Card>
      <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <CardTitle>Delivery Menu</CardTitle>
          <CardDescription>
            {enabledCount} of {items.length} items are available for delivery. Toggle any item to include or exclude it from the online delivery menu.
          </CardDescription>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => bulk("import_all")}
            data-testid="delivery-menu-import-all"
          >
            <DownloadSimple size={16} weight="bold" /> Import Entire Menu
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => bulk("clear_all")}
            data-testid="delivery-menu-clear-all"
          >
            <Prohibit size={16} weight="bold" /> Clear All
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative max-w-xs">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search items…"
            className="pl-9"
            data-testid="delivery-menu-search"
          />
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground py-6">Loading…</div>
        ) : (
          byCategory.map(([cat, list]) => (
            <div key={cat} data-testid={`delivery-menu-cat-${cat}`}>
              <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">
                {cat} · {list.filter((it) => it.available_for_delivery !== false).length}/{list.length}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {list.map((it) => {
                  const enabled = it.available_for_delivery !== false;
                  return (
                    <div
                      key={it.id}
                      className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-border"}`}
                      data-testid={`delivery-item-${it.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{it.name}</div>
                        <div className="text-xs text-muted-foreground">${it.price?.toFixed(2)}</div>
                      </div>
                      <Switch
                        checked={enabled}
                        disabled={savingId === it.id}
                        onCheckedChange={(v) => toggle(it, v)}
                        data-testid={`delivery-item-toggle-${it.id}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default DeliveryMenu;
